/** @file Emits IK records sorted by user `order` (stable) after the §6.5 chain rules (IK_INVALID). */
import { checkDocIk } from '@pose2d/format/doc/checkDocIk';
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import type { Doc, Pose2DError, Pose2DJson } from '@pose2d/format/types';
import { requireCompiledIndex } from './requireCompiledIndex';
import { throwFirstError } from './throwFirstError';

type IkJson = Pose2DJson['ik'][number];

/** IK records in ascending `order` (ties by name, R12-01) and the id -> index map. */
export function compileIk(doc: Doc, boneIndex: ReadonlyMap<string, number>): { ik: Pose2DJson['ik']; ikIndex: Map<string, number> } {
  const problems: Pose2DError[] = [];
  checkDocIk(doc, problems);
  throwFirstError(problems);
  const ordered = entriesByName(doc.constraints).sort(([, a], [, b]) => a.order - b.order);
  const ik = ordered.map(([id, c]): IkJson => ({
    name: c.name,
    bones: c.bones.map((b, i) => requireCompiledIndex(boneIndex, b, 'bone', `constraints.${id}.bones.${i}`)),
    target: requireCompiledIndex(boneIndex, c.target, 'bone', `constraints.${id}.target`),
    bend: c.bendPositive ? 1 : -1,
    mix: c.mix,
    softness: c.softness,
    stretch: c.stretch,
    compress: c.compress,
    uniform: c.uniform,
  }));
  return { ik, ikIndex: new Map(ordered.map(([id], i) => [id, i])) };
}
